<template>
  <section class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6">
    <div class="tw-flex items-center justify-between mb-4">
      <h3 class="text-base font-semibold text-gray-900 m-0">Inspire ({{ spottingAreasLabel }} and {{ productsLabel }})</h3>
    </div>
    <p class="text-sm text-gray-500 mt-0 mb-3">The graph below shows the evolution of number of all actors.</p>
    <div v-if="actors" class="chart-container">
      <div class="chart-main">
        <SimpleLineChart :data="actorsChartData">
        </SimpleLineChart>
      </div>

      <div class="chart-aside">
        <EcosystemInfoCard>
          <div class="text-muted">
            In total
          </div>
          <span class="text-lg">
            {{ totalActors }} actors
          </span>
          <div class="text-muted">
            are added
          </div>
        </EcosystemInfoCard>

        <ChartLegend :data="actorsChartData">
        </ChartLegend>
      </div>
    </div>

    <template v-if="claims && claims.total_claims">
      <p class="text-sm text-gray-500 mt-6 mb-3">The graph below shows the evolution of number of claimed actors</p>

      <div class="chart-container">
        <div class="chart-main chart-main-right">
          <SimpleLineChart :data="claimsChartData">
          </SimpleLineChart>
        </div>

        <div class="chart-aside chart-aside-left">
          <EcosystemInfoCard>
            <div class="text-muted">
              In total
            </div>
            <span class="text-lg">
              {{ claims.total_claims }} actors
            </span>
            <div class="text-muted">
              are claimed
            </div>
          </EcosystemInfoCard>

          <ChartLegend :data="claimsChartData">
          </ChartLegend>
        </div>
      </div>
    </template>

    <div v-if="showMostPopularActors" class="mt-6">
      <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Most popular actors / products</h4>
      <div class="overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead>
          <tr class="border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
            <th class="py-2 pr-4 font-semibold">Actor name</th>
            <th class="py-2 pr-4 font-semibold">Actor Type</th>
            <th class="py-2 pr-4 font-semibold">Page views</th>
            <th class="py-2 font-semibold">Unique Visits</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="page in mostPopularActors" class="border-b border-gray-100 hover:bg-gray-50">
            <td class="py-2 pr-4"><a class="text-primary hover:underline" :href="convertUrl(page.url)">{{ page.actor.name || convertUrl(page.url) }}</a></td>
            <td class="py-2 pr-4 text-gray-600">{{ page.actor.actor_type ? page.actor.actor_type : 'unknown' }}</td>
            <td class="py-2 pr-4 text-gray-600">{{ page.views || page.visits }}</td>
            <td class="py-2 text-gray-600">{{ page.unique_views || page.unique_visitors }}</td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
  import ChartLegend from './ChartLegend.vue'
  import EcosystemInfoCard from './EcosystemInfoCard.vue'
  import SimpleLineChart from '../../../components/Chart/SimpleLineChart.vue'
  import { parseChartData } from '../../Chart/chartsMixin.js'
  import TranslationsMixin from '../../../util/TranslationsMixin.js'

  const sum = (a, b) => a + b
  const sumValues = obj => Object.values(obj).reduce(sum)

  export default {
    props: {
      actors: Object,
      claims: Object,
      mostPopularActors: Object,
      mostPopularProducts: Object,
    },
    components: {
      ChartLegend,
      EcosystemInfoCard,
      SimpleLineChart,
    },
    data () {
      return {
        showMostPopularActors: false,
      }
    },
    computed: {
      actorsChartData () {
        if (!!this.mostPopularActors && typeof this.mostPopularActors === 'object') {
          this.showMostPopularActors = Object.keys(this.mostPopularActors).length > 0
        }
        const datasets = []

        if (!Array.isArray(this.actors.legal_entity_count)) {
          datasets.push({
            color: '#FBA52C',
            data: this.actors.legal_entity_count,
            cumulate: true,
            label: 'Organizations',
          })
        }

        if (!Array.isArray(this.actors.person_count)) {
          datasets.push({
            color: '#2E59FA',
            data: this.actors.person_count,
            cumulate: true,
            label: 'Persons',
          })
        }

        if (!Array.isArray(this.actors.product_count)) {
          datasets.push({
            color: '#AF52FF',
            data: this.actors.product_count,
            cumulate: true,
            label: 'Products',
          })
        }

        if (datasets.length > 0) {
          return parseChartData(datasets)
        } else {
          return null
        }
      },
      totalActors () {
        if (!this.actors) {
          return 0
        }
        return this.actors.total_legal_entities +
          this.actors.total_persons +
          this.actors.total_products
      },
      claimsChartData () {
        const datasets = []

        if (!this.claims) {
          return
        }

        if (!Array.isArray(this.claims.product_claim_count)) {
          datasets.push({
            color: '#B36ADD',
            data: this.claims.product_claim_count,
            cumulate: true,
            label: 'Products',
          })
        }

        if (!Array.isArray(this.claims.company_claim_count)) {
          datasets.push({
            color: '#FBA52C',
            data: this.claims.company_claim_count,
            cumulate: true,
            label: 'Organizations',
          })
        }

        if (!Array.isArray(this.claims.person_claim_count)) {
          datasets.push({
            color: '#2E59FA',
            data: this.claims.person_claim_count,
            cumulate: true,
            label: 'Individuals',
          })
        }

        if (datasets.length > 0) {
          return parseChartData(datasets)
        }

        return null
      },
    },
    methods: {
      convertUrl (pageUrl) {
        return `${window.location.protocol}//${window.location.hostname}${pageUrl}`
      },
    },
    mixins: [TranslationsMixin],
  }
</script>
